import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import AdvancedTable from "@/components/table/AdvancedTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, User as UserIcon, ListChecks, ExternalLink, Sparkles } from "lucide-react";

const STATUSES = ["open", "in_progress", "blocked", "done"];

export default function AllActionItems() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (onlyMine) params.set("only_mine", "true");
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/action-items?${params.toString()}`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, q, status, onlyMine]);

  const patch = async (id, p) => {
    await api.patch(`/tasks/${id}`, p);
    await load();
  };

  const columns = useMemo(() => ([
    {
      key: "title", label: "Action item", width: 320, sortable: true, filterType: "text", frozen: true,
      accessor: (t) => t.title,
      render: (t) => (
        <div>
          <div className="font-medium truncate">{t.title}</div>
          <div className="text-xs text-muted-foreground truncate">
            {t.client_name ? <span className="text-foreground/70">{t.client_name}</span> : null}
            {t.client_name && t.project_name ? " · " : null}
            {t.project_name}
          </div>
          {t.assignee_hint && !t.assignee_id && (
            <span className="chip text-[10px] inline-flex items-center gap-1 mt-1 bg-primary/10 text-primary border-primary/30" data-testid={`action-hint-${t.id}`}>
              <Sparkles className="w-3 h-3" /> Suggested: {t.assignee_hint}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "meeting_title", label: "From meeting", width: 200, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.meeting_title,
      render: (t) => (
        <div className="min-w-0">
          <Link to={`/app/meetings/${t.source_meeting_id}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline truncate text-xs">
            {t.meeting_title}
          </Link>
          <div className="text-[11px] text-muted-foreground">{t.meeting_at ? new Date(t.meeting_at).toLocaleDateString() : "—"}</div>
        </div>
      ),
    },
    {
      key: "due_date", label: "Due", width: 140, sortable: true, filterType: "date-range",
      accessor: (t) => t.due_date || "",
      stopPropagation: true,
      render: (t) => (
        <Input
          type="date"
          defaultValue={t.due_date?.slice(0, 10) || ""}
          onBlur={(e) => patch(t.id, { due_date: e.target.value || null })}
          className="h-7 text-xs"
          data-testid={`action-due-${t.id}`}
        />
      ),
    },
    {
      key: "status", label: "Status", width: 140, sortable: true, groupable: true,
      filterType: "select", filterOptions: STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") })),
      accessor: (t) => t.status,
      stopPropagation: true,
      render: (t) => (
        <Select value={t.status} onValueChange={(v) => patch(t.id, { status: v })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`action-status-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
      ),
    },
    {
      key: "open", label: " ", width: 60, align: "right",
      render: (t) => (
        <Link to={`/app/meetings/${t.source_meeting_id}`} onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary inline-block" data-testid={`action-open-${t.id}`}>
          <ExternalLink className="w-3 h-3" />
        </Link>
      ),
    },
  ]), []);

  return (
    <>
      <PageHeader
        eyebrow="Comms"
        title="All action items"
        description="Every action item from every meeting across all projects you can see. Filter by status, assignee, or text. Sort, filter and pick columns — your view is saved per user."
        testid="all-actions-header"
      />
      <div className="px-4 sm:px-6 md:px-10">
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2" data-testid="all-actions-filters">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or description…" className="h-8 w-64 text-xs" data-testid="all-actions-q" />
          <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="all-actions-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant={onlyMine ? "default" : "outline"} onClick={() => setOnlyMine((v) => !v)} className="rounded-none h-8 text-xs" data-testid="all-actions-mine">
            <UserIcon className="w-3 h-3 mr-1" /> {onlyMine ? "Mine ✓" : "Mine"}
          </Button>
          <span className="text-[10px] text-muted-foreground ml-auto">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 py-6">
        <AdvancedTable
          tableKey="action-items"
          title="Action items"
          rows={rows}
          columns={columns}
          loading={busy && rows.length === 0}
          emptyIcon={ListChecks}
          emptyTitle="No action items"
          emptyDescription="Action items appear here when you add them from any meeting."
        />
      </div>
    </>
  );
}
